// Native share helper for Capacitor (Android/iOS).
//
// On native, navigator.share is unreliable inside the WebView and often missing,
// so directly saving a fall-through ends up dumping the image into the gallery
// instead of opening WhatsApp / Instagram / etc.
//
// This helper:
// 1. Downloads the image to a Blob
// 2. Writes it to the app's Cache directory via @capacitor/filesystem
// 3. Hands the resulting file:// URI to @capacitor/share, which opens the
//    native Android/iOS share sheet with WhatsApp, Instagram, Messages, etc.
//
// On the web it falls back to navigator.share with a File payload, and finally
// to a plain download as a last resort.
import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

export interface NativeShareOptions {
  url: string;
  filename: string;
  title?: string;
  text?: string;
  /** Optional dialog title shown on Android share sheet. */
  dialogTitle?: string;
}

export interface NativeShareResult {
  /** True if the share sheet was actually opened (or web share succeeded). */
  shared: boolean;
  /** True if the user cancelled / dismissed the share sheet. */
  cancelled?: boolean;
}

/**
 * Share an image through the OS share sheet on native, or Web Share API on web.
 * Throws on real errors; returns shared=false (without throwing) only when no
 * sharing path was available at all.
 */
export async function shareImageNative(
  opts: NativeShareOptions,
): Promise<NativeShareResult> {
  const { url, filename, title, text, dialogTitle } = opts;

  // ── Native (Android/iOS) ────────────────────────────────────────────
  if (Capacitor.isNativePlatform()) {
    const res = await fetch(url);
    const blob = await res.blob();
    const base64 = await blobToBase64(blob);

    // Write to Cache so we don't pollute the user's Documents/gallery.
    const written = await Filesystem.writeFile({
      path: filename,
      data: base64,
      directory: Directory.Cache,
    });

    try {
      await Share.share({
        title,
        text,
        url: written.uri, // file:// URI — Capacitor Share converts to content:// on Android
        dialogTitle: dialogTitle ?? "Share your look",
      });
      return { shared: true };
    } catch (e) {
      const msg = (e as Error)?.message ?? "";
      // User cancelled — treat as handled.
      if (/cancel/i.test(msg) || (e as Error)?.name === "AbortError") {
        return { shared: false, cancelled: true };
      }
      throw e;
    }
  }

  // ── Web fallback: Web Share API with File ───────────────────────────
  if (typeof navigator !== "undefined" && "share" in navigator) {
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const file = new File([blob], filename, {
        type: blob.type || "image/png",
      });
      const data: ShareData = { title, text, files: [file] };
      const canShare =
        "canShare" in navigator
          ? (navigator as Navigator & { canShare: (d: ShareData) => boolean }).canShare(data)
          : true;
      if (canShare) {
        await navigator.share(data);
        return { shared: true };
      }
      // Fall back to URL-only share if files aren't supported.
      await navigator.share({ title, text });
      return { shared: true };
    } catch (e) {
      if ((e as Error)?.name === "AbortError") {
        return { shared: false, cancelled: true };
      }
      // Fall through to download fallback below.
    }
  }

  return { shared: false };
}
